import React from 'react';
import { Truck, Package } from 'lucide-react';
import { Input } from '../../ui/input';
import {
  OnboardingFormData,
  PackagingType,
} from '../../../types/business';
import { cn } from '../../../lib/utils';

interface StepFulfillmentProps {
  formData: OnboardingFormData;
  updateField: <K extends keyof OnboardingFormData>(field: K, value: OnboardingFormData[K]) => void;
  error: string | null;
}

const PACKAGING_OPTIONS: Array<{
  id: PackagingType;
  label: string;
  description: string;
}> = [
  { id: 'standard', label: 'Standard', description: 'Regular bags or boxes' },
  { id: 'fragile', label: 'Fragile', description: 'Extra care needed' },
  { id: 'garment_bag', label: 'Garment Bag', description: 'Hanging garments' },
  { id: 'gift_wrap', label: 'Gift Wrap', description: 'Gift-wrapped items' },
];

export function StepFulfillment({ formData, updateField }: StepFulfillmentProps) {
  const fulfillment = formData.fulfillment;

  const updateFulfillment = (field: string, value: any) => {
    updateField('fulfillment', { ...fulfillment, [field]: value });
  };

  const togglePackaging = (pkg: PackagingType) => {
    const current = fulfillment.packaging;
    if (current.includes(pkg)) {
      if (current.length > 1) {
        updateFulfillment('packaging', current.filter((p: PackagingType) => p !== pkg));
      }
    } else {
      updateFulfillment('packaging', [...current, pkg]);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-serif font-semibold text-heading">
          Courier Pickup Details
        </h2>
        <p className="text-body mt-1">
          Help our couriers pick up your orders smoothly.
        </p>
      </div>

      {/* Uber Direct info */}
      <div className="bg-purple-50/50 border border-primary/10 rounded-card p-4 flex items-start gap-3">
        <Truck className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-heading">Deliveries powered by Uber Direct</p>
          <p className="text-xs text-body mt-1">
            When you approve an order, an Uber courier is automatically dispatched to
            your location. They'll confirm pickup and deliver directly to the customer.
            You'll be notified at every step.
          </p>
        </div>
      </div>

      {/* Packaging Types */}
      <div className="space-y-3">
        <div>
          <label className="text-sm font-medium text-heading block">
            Packaging Types
          </label>
          <p className="text-xs text-body mt-0.5">
            What types of packaging do your orders use? Couriers will handle items accordingly.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {PACKAGING_OPTIONS.map((pkg) => {
            const isSelected = fulfillment.packaging.includes(pkg.id);
            return (
              <button
                key={pkg.id}
                type="button"
                onClick={() => togglePackaging(pkg.id)}
                className={cn(
                  'flex flex-col p-3 rounded-xl border-2 transition-all text-left',
                  isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-100 hover:border-gray-200'
                )}
              >
                <span className="text-sm font-medium text-heading">{pkg.label}</span>
                <span className="text-xs text-body">{pkg.description}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Max Orders Per Hour */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-heading block">
          Max Orders Per Hour <span className="text-body font-normal">(optional)</span>
        </label>
        <p className="text-xs text-body">
          Leave empty for no limit. New orders will be paused when the cap is reached.
        </p>
        <div className="w-40">
          <Input
            type="number"
            placeholder="No limit"
            value={fulfillment.maxOrdersPerHour ?? ''}
            onChange={(e) =>
              updateFulfillment(
                'maxOrdersPerHour',
                e.target.value ? Number(e.target.value) : null
              )
            }
            min={1}
            max={100}
          />
        </div>
      </div>

      {/* Pickup Instructions */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-heading block">
          Store Pickup Instructions
        </label>
        <p className="text-xs text-body">
          Saved on your store and sent to every Uber courier at pickup — mall floor, suite, or entrance details.
        </p>
        <textarea
          placeholder="e.g., Inside the mall on the second floor next to Nordstrom"
          value={fulfillment.pickupInstructions}
          onChange={(e) => updateFulfillment('pickupInstructions', e.target.value.slice(0, 280))}
          rows={3}
          maxLength={280}
          className="flex w-full rounded-input border border-gray-200 bg-white px-3 py-3 text-sm ring-offset-white placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 font-sans resize-none"
        />
        <p className="text-xs text-body text-right">{fulfillment.pickupInstructions.length}/280</p>
      </div>
    </div>
  );
}
